import _                                from 'underscore';
import React, { ReactNode, useReducer } from 'react';
import { produce }                      from 'immer';
import { v4 as uuid }                   from 'uuid';

import { CONTEXTS } from '../constants/ContextEnums';
import { IToast }   from '../views/components/toast-card/ToastCard';

enum ACTIONS {
	ADD        = 'add',
	ERROR      = 'error',
	SUCCESS    = 'success',
	INFO       = 'info',
	WARNING    = 'warning',
	REMOVEBYID = 'removeById',
	REMOVEALL  = 'removeAll',
}

export interface ToastAction {
	type: ACTIONS,
	value?: string | number | ToastValueOpType
}

export interface ToastActionValue {
	title: string | number | ReactNode,
	message?: string | number | ReactNode,
	type: CONTEXTS
}

export interface ToastState {
	items: any,
	queuedForRemoval: any[]
}

export type ToastMessageFunction = (
	title: ToastActionValue['title'],
	message?: ToastActionValue['message'],
	options?: Omit<IToast, 'title' | 'message' | 'type'>
) => void;

export type ToastValueOpType = ToastActionValue;
export type ToastValueOpFunction<T = ToastValueOpType> = ( value: T ) => void;

export type ToastsHook = [ ToastState, ToastHookActions ];

export interface ToastHookActions {
	add: ToastValueOpFunction<ToastActionValue>,
	error: ToastMessageFunction,
	success: ToastMessageFunction,
	info: ToastMessageFunction,
	warning: ToastMessageFunction,
	removeById: ToastValueOpFunction<string | number>,
	removeAll: Function,
}

let actionReducer = function reducer( state: ToastState, action: ToastAction ){
	
	switch( action.type ){
		
		case ACTIONS.ADD:
		case ACTIONS.INFO:
		case ACTIONS.SUCCESS:
		case ACTIONS.ERROR:
		case ACTIONS.WARNING:
			
			return produce( state, draft => {
				draft.items = [ ...draft.items, { id: uuid(), ...( action.value as object ) } ]
			} );
		
		case ACTIONS.REMOVEBYID:
			
			return produce( state, draft => {
				draft.items = _.reject( draft.items, { id: action.value } )
			} );
		
		case ACTIONS.REMOVEALL:
			
			return produce( state, draft => {
				draft.items = []
			} );
		
		default:
			throw new Error();
	}
};

export const useToasts = ( initialState = {} ): ToastsHook => {
	
	let state: ToastState = {
		items:            [],
		queuedForRemoval: [],
		...initialState
	};
	
	let actions: ToastHookActions = {
		[ ACTIONS.ADD ]:        ( value ) => _dispatch( { type: ACTIONS.ADD, value } ),
		[ ACTIONS.REMOVEBYID ]: ( value ) => _dispatch( { type: ACTIONS.REMOVEBYID, value } ),
		[ ACTIONS.INFO ]:       ( title, message, options = {} ) => _dispatch( { type: ACTIONS.INFO, value: { title, message, ...options, type: CONTEXTS.INFO } } ),
		[ ACTIONS.SUCCESS ]:    ( title, message, options = {} ) => _dispatch( { type: ACTIONS.SUCCESS, value: { title, message, ...options, type: CONTEXTS.SUCCESS } } ),
		[ ACTIONS.ERROR ]:      ( title, message, options = {} ) => _dispatch( { type: ACTIONS.ERROR, value: { title, message, ...options, type: CONTEXTS.ERROR } } ),
		[ ACTIONS.WARNING ]:    ( title, message, options = {} ) => _dispatch( { type: ACTIONS.WARNING, value: { title, message, ...options, type: CONTEXTS.WARNING } } ),
		[ ACTIONS.REMOVEALL ]:  () => _dispatch( { type: ACTIONS.REMOVEALL } ),
	};
	
	const [ _state, _dispatch ] = useReducer( actionReducer, state );
	
	// [ ToastState, ToastHookActions ]
	return [ _state, actions ]
};
